/**
 * @file Uppy factory for the upload manager.
 * Configures GoldenRetriever, S3 multipart (ZIP), XHR (single files), and composite routing.
 */

import type { UppyFile } from '@uppy/core';
import Uppy from '@uppy/core';
import GoldenRetriever from '@uppy/golden-retriever';
import XHRUpload from '@uppy/xhr-upload';

import {
  configureS3MultipartPlugin,
  type PartUrlStore,
} from './adapters/create-s3-multipart-plugin';
import { isZipUppyFile } from './adapters/is-zip-uppy-file';
import { UPPY_INSTANCE_ID } from './constants';
import {
  installIncompleteUploadPersistence,
  migrateLegacyGoldenRetrieverMetadata,
} from './golden-retriever-recovery';
import {
  deleteMultipartContext,
  saveMultipartContext,
} from './persist-upload-sessions';
import {
  getUploadKindAdapter,
  getXhrAllowedMetaFields,
} from './upload-kind-registry';

import type { PersistedMultipartContext } from './types';

type Meta = Record<string, unknown>;
type Body = Record<string, unknown>;

type UploaderFn = (fileIDs: string[]) => Promise<unknown>;

/** Options for {@link createUploadManagerUppy}. */
export type CreateUploadManagerUppyOptions = {
  /** Shared part URL store for S3 multipart uploads. */
  partUrlStore: PartUrlStore;
  /** Shared map of persisted multipart registration contexts. */
  persistedContexts: Map<string, PersistedMultipartContext>;
};

const isPendingUpload = (file: UppyFile<Meta, Body>) =>
  !file.progress?.uploadComplete && file.error == null;

const resolveUploadKind = (fileMeta: Record<string, unknown>): string =>
  String(fileMeta.uploadKind ?? '');

/**
 * Creates and configures the upload manager Uppy instance.
 * Routes ZIP files to S3 multipart and other files to XHR per adapter registry.
 */
export const createUploadManagerUppy = ({
  partUrlStore,
  persistedContexts,
}: CreateUploadManagerUppyOptions) => {
  const uppy = new Uppy<Meta, Body>({
    id: UPPY_INSTANCE_ID,
    autoProceed: false,
    allowMultipleUploadBatches: true,
    restrictions: {
      maxNumberOfFiles: null,
    },
  });

  let awsUploader: UploaderFn | undefined;
  let xhrUploader: UploaderFn | undefined;
  const originalAddUploader = uppy.addUploader.bind(uppy);

  uppy.addUploader = ((fn: UploaderFn) => {
    if (!awsUploader) {
      awsUploader = fn;
    } else if (!xhrUploader) {
      xhrUploader = fn;
    }
    return originalAddUploader(fn);
  }) as typeof uppy.addUploader;

  migrateLegacyGoldenRetrieverMetadata();
  uppy.use(GoldenRetriever, { serviceWorker: true });
  installIncompleteUploadPersistence(uppy);

  configureS3MultipartPlugin(uppy, {
    partUrlStore,
    resolveHandlers: (file) => {
      const adapter = getUploadKindAdapter(resolveUploadKind(file.meta));
      if (!adapter?.resolveMultipartHandlers) {
        throw new Error(
          `No multipart handlers for upload kind: ${resolveUploadKind(file.meta)}`
        );
      }
      return adapter.resolveMultipartHandlers(file.meta);
    },
    getPersistedContext: (uppyFileId) => persistedContexts.get(uppyFileId),
    onRegister: async (context) => {
      persistedContexts.set(context.uppyFileId, context);
      await saveMultipartContext(context);
    },
    onFinalize: async (uppyFileId) => {
      persistedContexts.delete(uppyFileId);
      await deleteMultipartContext(uppyFileId);
    },
    shouldUseMultipart: isZipUppyFile,
  });

  uppy.use(XHRUpload, {
    id: 'XHRUpload',
    endpoint: (fileOrBundle) => {
      const file = Array.isArray(fileOrBundle) ? fileOrBundle[0] : fileOrBundle;
      const adapter = getUploadKindAdapter(resolveUploadKind(file.meta));
      if (!adapter?.resolveXhrEndpoint) {
        throw new Error(
          `No XHR endpoint for upload kind: ${resolveUploadKind(file.meta)}`
        );
      }
      return adapter.resolveXhrEndpoint(file.meta);
    },
    method: 'POST',
    formData: true,
    fieldName: 'file',
    allowedMetaFields: getXhrAllowedMetaFields(),
    getResponseData: (xhr) => ({ url: xhr.responseURL || '' }),
  });

  uppy.addUploader = originalAddUploader;

  if (awsUploader) {
    uppy.removeUploader(awsUploader);
  }
  if (xhrUploader) {
    uppy.removeUploader(xhrUploader);
  }

  uppy.addUploader(async (fileIDs) => {
    const files = uppy.getFilesByIds(fileIDs).filter(isPendingUpload);
    const zipFileIds = files.filter(isZipUppyFile).map((file) => file.id);
    const documentFileIds = files
      .filter((file) => !isZipUppyFile(file))
      .map((file) => file.id);

    await Promise.all([
      zipFileIds.length > 0 && awsUploader
        ? awsUploader(zipFileIds)
        : Promise.resolve(),
      documentFileIds.length > 0 && xhrUploader
        ? xhrUploader(documentFileIds)
        : Promise.resolve(),
    ]);
  });

  return uppy;
};
